import React, { useState } from 'react'
import apiService from '../services/apiService'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { cn } from '../lib/utils'
import {
  Rocket,
  Globe,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Copy,
  Loader2,
  Lightbulb,
  PartyPopper
} from 'lucide-react'

interface QuickStartProps {
  onComplete: (siteId: string) => void
  onSkip?: () => void
}

const QuickStart: React.FC<QuickStartProps> = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(1)
  const [siteData, setSiteData] = useState({
    name: '',
    domain: '',
    languages: ['es', 'fr']
  })
  const [loading, setLoading] = useState(false)
  const [generatedCode, setGeneratedCode] = useState('')
  const [copied, setCopied] = useState(false)

  const availableLanguages = [
    { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Portuguese', native: 'Português', flag: '🇵🇹' },
    { code: 'it', name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
    { code: 'zh', name: 'Chinese', native: '中文', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', native: '日本語', flag: '🇯🇵' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ar', name: 'Arabic', native: 'العربية', flag: '🇸🇦' },
    { code: 'ru', name: 'Russian', native: 'Русский', flag: '🇷🇺' }
  ]

  const handleLanguageToggle = (langCode: string) => {
    setSiteData(prev => ({
      ...prev,
      languages: prev.languages.includes(langCode)
        ? prev.languages.filter(l => l !== langCode)
        : [...prev.languages, langCode]
    }))
  }

  const createSite = async () => {
    setLoading(true)
    try {
      const response = await apiService.post('/sites', siteData)
      const siteId = response.data.data.id
      const apiKey = response.data.data.apiKey

      const code = `<script src="https://cdn.localizationhub.com/sdk/localize.js?site=${siteId}&key=${apiKey}"></script>`
      setGeneratedCode(code)
      setStep(4)
    } catch (error) {
      console.error('Error creating site:', error)
      alert('Failed to create site. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  if (step === 1) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Rocket className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Quick Start</CardTitle>
          <CardDescription>Get your website multilingual in 3 minutes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Website Name</Label>
            <Input
              id="name"
              value={siteData.name}
              onChange={(e) => setSiteData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="My Awesome Website"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="domain">Website Domain</Label>
            <Input
              id="domain"
              type="url"
              value={siteData.domain}
              onChange={(e) => setSiteData(prev => ({ ...prev, domain: e.target.value }))}
              placeholder="https://example.com"
            />
          </div>

          <div className="flex justify-between pt-4">
            {onSkip && (
              <Button variant="ghost" onClick={onSkip}>
                Skip Setup
              </Button>
            )}
            <Button
              onClick={() => setStep(2)}
              disabled={!siteData.name || !siteData.domain}
              className="ml-auto"
            >
              Next Step
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (step === 2) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Choose Languages</CardTitle>
          <CardDescription>Select the languages you want to support</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageToggle(lang.code)}
                className={cn(
                  "p-3 rounded-lg border-2 text-left transition-all",
                  siteData.languages.includes(lang.code)
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground"
                )}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{lang.flag}</span>
                  <div>
                    <div className="font-medium text-sm text-foreground">{lang.name}</div>
                    <div className="text-xs text-muted-foreground">{lang.native}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-primary/5 rounded-lg p-3 mb-6 flex items-start">
            <Lightbulb className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              <strong>Pro tip:</strong> Start with 2-3 languages to test the waters. You can always add more later!
            </p>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={() => setStep(3)}
              disabled={siteData.languages.length === 0}
            >
              Continue ({siteData.languages.length} selected)
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (step === 3) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <CheckCircle className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Ready to Deploy</CardTitle>
          <CardDescription>Review your setup and create your multilingual site</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 rounded-lg p-4 mb-6 space-y-3">
            <div>
              <span className="font-medium text-foreground">Website:</span>
              <p className="text-foreground">{siteData.name}</p>
              <p className="text-sm text-muted-foreground">{siteData.domain}</p>
            </div>

            <div>
              <span className="font-medium text-foreground">Languages ({siteData.languages.length}):</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {siteData.languages.map(langCode => {
                  const lang = availableLanguages.find(l => l.code === langCode)
                  return (
                    <Badge key={langCode} variant="secondary">
                      {lang?.flag} {lang?.name}
                    </Badge>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-start">
            <PartyPopper className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">
              <strong>You're all set!</strong> Click create to generate your integration code.
            </p>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={createSite}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Site'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (step === 4) {
    return (
      <Card className="max-w-lg mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle>Site Created!</CardTitle>
          <CardDescription>Add this code to your website to enable multilingual support</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-zinc-900 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-start mb-2">
              <span className="text-green-400 font-mono text-sm">HTML</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyCode}
                className="text-zinc-400 hover:text-white -mr-2 -mt-1"
              >
                <Copy className="h-4 w-4 mr-1" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <code className="text-green-300 text-sm break-all block">
              {generatedCode}
            </code>
          </div>

          <div className="space-y-3 mb-6 text-sm">
            <div className="flex items-start">
              <Badge className="mr-2 w-6 h-6 flex items-center justify-center p-0">1</Badge>
              <span className="text-muted-foreground">Copy the code above and paste it in your website's <code className="bg-muted px-1 rounded">&lt;head&gt;</code> section</span>
            </div>
            <div className="flex items-start">
              <Badge className="mr-2 w-6 h-6 flex items-center justify-center p-0">2</Badge>
              <span className="text-muted-foreground">The language switcher will automatically appear on your site</span>
            </div>
            <div className="flex items-start">
              <Badge className="mr-2 w-6 h-6 flex items-center justify-center p-0">3</Badge>
              <span className="text-muted-foreground">Start adding translations in your dashboard</span>
            </div>
          </div>

          <div className="bg-primary/5 rounded-lg p-3 mb-6 flex items-start">
            <Lightbulb className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              <strong>Need help?</strong> Check out our documentation or contact support for assistance.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={() => onComplete('success')}
              className="flex-1"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={copyCode}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Code
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return null
}

export default QuickStart
